export function cleanGeoJSONForCesium(input, forceZeroZ = false) {
  const validateGeoJSON = (geojson) => {
    if (!geojson || typeof geojson !== "object") return false;
    const checkTuple = (arr) => Array.isArray(arr) && arr.length >= 2 && Number.isFinite(Number(arr[0])) && Number.isFinite(Number(arr[1]));
    const checkCoordsDeep = (coords) => {
      if (!Array.isArray(coords)) return true;
      if (checkTuple(coords)) return true;
      return coords.every((c) => checkCoordsDeep(c));
    };
    const checkGeometry = (geom) => {
      if (!geom || typeof geom !== "object") return false;
      if (Array.isArray(geom.coordinates)) return checkCoordsDeep(geom.coordinates);
      if (geom.type === "GeometryCollection" && Array.isArray(geom.geometries)) return geom.geometries.every((g) => checkGeometry(g));
      return true;
    };
    if (geojson.type === "FeatureCollection" && Array.isArray(geojson.features)) return geojson.features.every((f) => checkGeometry(f.geometry));
    else if (geojson.type === "Feature") return checkGeometry(geojson.geometry);
    else return checkGeometry(geojson);
  };

  const truncateTo = (num, dec) => {
    if (!Number.isFinite(num)) return NaN;
    const f = Math.pow(10, dec);
    return (num < 0 ? Math.ceil(num * f) : Math.floor(num * f)) / f;
  };

  const round8ThenTrunc7 = (num) => {
    if (!Number.isFinite(num)) return NaN; // simple guard

    const f8 = 1e8;
    const f7 = 1e7;

    const r8 = Math.round(num * f8) / f8; // round to 8 decimals
    return (r8 < 0 ? Math.ceil(r8 * f7) : Math.floor(r8 * f7)) / f7; // truncate to 7
  };

  const normalizePosition = (coord) => {
    if (!Array.isArray(coord)) return null;
    const lon = Number(coord[0]);
    const lat = Number(coord[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;

    const lonT = round8ThenTrunc7(lon);
    const latT = round8ThenTrunc7(lat);

    let z = 0;
    if (!forceZeroZ) {
      const zRaw = coord.length >= 3 ? Number(coord[2]) : 0;
      z = Number.isFinite(zRaw) ? truncateTo(zRaw, 2) : 0; 
    }

    return [lonT, latT, z];
  };

  const dedupeConsecutive = (positions) => {
    const out = [];
    for (let i = 0; i < positions.length; i++) {
      const p = positions[i];
      if (!p) continue;
      if (out.length === 0) out.push(p);
      else {
        const q = out[out.length - 1];
        if (p[0] !== q[0] || p[1] !== q[1] || p[2] !== q[2]) out.push(p);
      }
    }
    return out;
  };

  const normalizeRing = (ring) => {
    let pts = (ring || []).map(normalizePosition).filter(Boolean);
    pts = dedupeConsecutive(pts);
    if (pts.length < 4) return null;

    const first = pts[0];
    const last = pts[pts.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1] || first[2] !== last[2]) {
      pts.push([first[0], first[1], first[2]]);
    }
    if (pts.length < 4) return null;
    return pts;
  };

  const normalizeLine = (coords) => {
    let pts = (coords || []).map(normalizePosition).filter(Boolean);
    pts = dedupeConsecutive(pts);
    if (pts.length < 2) return null;
    return pts;
  };

  const normalizeGeometry = (geom) => {
    if (!geom || typeof geom !== "object") return null;
    const type = geom.type;
    if (type === "GeometryCollection") {
      const geoms = (geom.geometries || []).map(normalizeGeometry).filter(Boolean);
      return geoms.length ? { type, geometries: geoms } : null;
    }

    const c = geom.coordinates;
    switch (type) {
      case "Point": {
        const pos = normalizePosition(c);
        return pos ? { type, coordinates: pos } : null;
      }
      case "MultiPoint": {
        const pts = (c || []).map(normalizePosition).filter(Boolean);
        return pts.length ? { type, coordinates: pts } : null;
      }
      case "LineString": {
        const line = normalizeLine(c);
        return line ? { type, coordinates: line } : null;
      }
      case "MultiLineString": {
        const lines = (c || []).map(normalizeLine).filter(Boolean);
        return lines.length ? { type, coordinates: lines } : null;
      }
      case "Polygon": {
        const rings = (c || []).map(normalizeRing).filter(Boolean);
        return rings.length ? { type, coordinates: rings } : null;
      }
      case "MultiPolygon": {
        const polys = (c || [])
          .map((poly) => {
            const rings = (poly || []).map(normalizeRing).filter(Boolean);
            return rings.length ? rings : null;
          })
          .filter(Boolean);
        return polys.length ? { type, coordinates: polys } : null;
      }
      default:
        return null;
    }
  };

  const normalizeFeature = (feat) => {
    if (!feat || typeof feat !== "object") return null;

    if (feat.type !== "Feature") {
      const g2 = normalizeGeometry(feat);
      return g2 ? { type: "Feature", geometry: g2, properties: {} } : null;
    }

    const g = normalizeGeometry(feat.geometry);
    if (!g) return null;

    const out = {
      type: "Feature",
      geometry: g,
      properties: feat.properties && typeof feat.properties === "object" ? { ...feat.properties } : {},
    };
    if (feat.id !== undefined) out.id = feat.id;
    return out;
  };

  // preserve extra root-level keys when converting to FeatureCollection
  const toFeatureCollection = (root) => {
    if (!root || typeof root !== "object") throw new Error("Invalid GeoJSON input.");

    // FeatureCollection input: keep other root keys, but replace features with normalized ones
    if (root.type === "FeatureCollection") {
      const features = (root.features || []).map(normalizeFeature).filter(Boolean);
      return { ...root, type: "FeatureCollection", features }; // keep extra root props
    }

    // Feature input: normalize and preserve other top-level keys except geometry/properties/type
    if (root.type === "Feature") {
      const f = normalizeFeature(root);
      const extra = { ...root };
      delete extra.type;
      delete extra.geometry;
      delete extra.properties;
      // keep other root keys and place normalized features
      return { type: "FeatureCollection", features: f ? [f] : [], ...extra };
    }

    // Geometry or other object: normalize and preserve extra root keys except geometry/type/coordinates
    const g = normalizeGeometry(root);
    if (!g) {
      return { type: "FeatureCollection", features: [] };
    }
    const extra = { ...root };
    delete extra.type;
    delete extra.coordinates;
    delete extra.geometries;
    return {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: g, properties: {} }],
      ...extra,
    };
  };

  let obj;
  if (typeof input === "string") {
    try {
      obj = JSON.parse(input);
    } catch (e) {
      // console.error("GeoJSON string is not valid JSON.");
      return { type: "FeatureCollection", features: [] };
    }
  } else {
    obj = input;
  }

  if (!validateGeoJSON(obj)) {
    // console.error("Invalid GeoJSON structure.");
    return { type: "FeatureCollection", features: [] };
  }

  const fc = toFeatureCollection(obj);
  return fc;
}
